import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import axios from 'axios'
import connectApi from '../../api/connectApi'
import { constants, getConnectUser, internetConnect } from 'helpers'
import { sendMessageToWorker } from '../../helpers/communication'
import { ZoneInfoTemplate } from 'components/enrollForm'

const { ENROLLMENT, COUNTRY_CONFIG } = constants.endpoints

export interface FcHomeState {
    loading: boolean
    enrollmentData: ZoneInfoTemplate[] | undefined
    error: string | undefined
}

type SuccessfulEnrollment = ZoneInfoTemplate[]
type FailedRequest = { error: string }

const initialState: FcHomeState = {
    loading: false,
    enrollmentData: undefined,
    error: undefined
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

/** connectApi rejects only network/timeout failures (no response). */
const isNetworkFailure = (error: unknown) =>
    axios.isAxiosError(error) &&
    !error.response &&
    (error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED' ||
        error.message === 'Network Error')

export const fetchCountryEnrollment = createAsyncThunk<
    SuccessfulEnrollment,
    void,
    { rejectValue: FailedRequest }
>('fcHome/fetchCountryEnrollment', async (_, thunkAPI) => {
    if (!internetConnect()) {
        return thunkAPI.rejectWithValue({ error: 'Offline' })
    }

    try {
        const response = await connectApi.get(ENROLLMENT)

        if (!response || response.status >= 400 || response.status === 0) {
            return thunkAPI.rejectWithValue({
                error: response?.data?.detail || 'Enrollment request failed'
            })
        }

        const productCategoryPayload = response.data?.[0]?.product_list ?? []

        if (productCategoryPayload.length) {
            sendMessageToWorker({
                [constants.MESSAGES.STORE_PRODUCT_CATEGORY]:
                    productCategoryPayload
            })
        }

        return productCategoryPayload
    } catch (error) {
        if (!isNetworkFailure(error)) {
            Sentry.captureException(error)
        }
        return thunkAPI.rejectWithValue({
            error: 'Failed to fetch enrollment form'
        })
    }
})

/** ATC/FC: load country_config only (not full VE homeAction). */
export const fetchCountryConfig = createAsyncThunk<
    Record<string, unknown>,
    void,
    { rejectValue: FailedRequest }
>('fcHome/fetchCountryConfig', async (_, thunkAPI) => {
    if (!internetConnect()) {
        return thunkAPI.rejectWithValue({ error: 'Offline' })
    }

    const { country } = getConnectUser()
    if (!country) {
        return thunkAPI.rejectWithValue({ error: 'Missing country' })
    }

    try {
        const response = await connectApi.get(COUNTRY_CONFIG, {
            params: { country }
        })

        if (!response || response.status >= 400 || response.status === 0) {
            return thunkAPI.rejectWithValue({
                error: response?.data?.detail || 'Country config request failed'
            })
        }

        if (!isPlainObject(response.data)) {
            return thunkAPI.rejectWithValue({
                error: 'Invalid country config response'
            })
        }

        localStorage.setItem(
            constants.COUNTRY_CONFIG,
            JSON.stringify(response.data)
        )
        return response.data
    } catch (error) {
        if (!isNetworkFailure(error)) {
            Sentry.captureException(error)
        }
        return thunkAPI.rejectWithValue({
            error: 'Failed to fetch country config'
        })
    }
})

export const fcHomeSlice = createSlice({
    name: 'fcHome',
    initialState,
    reducers: {
        resetEnrollmentState: () => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCountryEnrollment.pending, (state) => {
                state.loading = true
                state.error = undefined
            })
            .addCase(
                fetchCountryEnrollment.fulfilled,
                (state, action: PayloadAction<SuccessfulEnrollment>) => {
                    state.loading = false
                    state.enrollmentData = action.payload
                    state.error = undefined
                }
            )
            .addCase(
                fetchCountryEnrollment.rejected,
                (state, action: PayloadAction<FailedRequest | undefined>) => {
                    state.loading = false
                    state.enrollmentData = undefined
                    state.error =
                        action.payload?.error ||
                        'Failed to fetch enrollment form'
                }
            )
    }
})

export const { resetEnrollmentState } = fcHomeSlice.actions

export default fcHomeSlice.reducer
